import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const ALLOWED = ['principal', 'admin', 'admin1'];
const BASE = `${API_BASE_URL}/api/bulk-data`;

function Dropzone({ onFile, busy }) {
  const ref = useRef(null);
  const [over, setOver] = useState(false);
  const pick = (f) => f && onFile(f);
  return (
    <div
      onClick={() => !busy && ref.current.click()}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); if (!busy) pick(e.dataTransfer.files[0]); }}
      className={`cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition ${
        over ? 'border-indigo-500 bg-indigo-50' : 'border-slate-300 hover:border-indigo-400 hover:bg-slate-50'
      } ${busy ? 'opacity-60 cursor-wait' : ''}`}
    >
      <div className="text-3xl mb-1">📤</div>
      <p className="text-sm font-semibold text-slate-700">{busy ? 'Uploading...' : 'Drop filled Excel here or click to browse'}</p>
      <p className="text-xs text-slate-500 mt-1">.xlsx / .xls, max 5 MB</p>
      <input ref={ref} type="file" accept=".xlsx,.xls" className="hidden"
        onChange={(e) => { pick(e.target.files[0]); e.target.value = ''; }} />
    </div>
  );
}

export default function BulkDataManager({ user }) {
  const [classes, setClasses] = useState([]);
  const [className, setClassName] = useState('');
  const [busy, setBusy] = useState('');
  const [toast, setToast] = useState(null);
  const [details, setDetails] = useState([]);

  const allowed = ALLOWED.includes(user?.role);
  const headers = {
    'x-user-role': user?.role || '',
    'x-user-name': user?.name || user?.email || ''
  };

  const notify = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  };

  useEffect(() => {
    if (!allowed) return;
    axios.get(`${API_BASE_URL}/api/attendance-module/classes`, { headers })
      .then((r) => setClasses(r.data))
      .catch(() => notify('error', 'Could not load classes'));
    // eslint-disable-next-line
  }, [allowed]);

  if (!allowed) {
    return <div className="p-6 text-center text-red-600 font-semibold">Access denied. Only principal, admin and admin1 can open this page.</div>;
  }

  const errMsg = async (e) => {
    const d = e.response?.data;
    if (d instanceof Blob) { try { return JSON.parse(await d.text()).error; } catch (_) { return 'Request failed'; } }
    return d?.error || e.message;
  };

  const download = async (path, params, key) => {
    setBusy(key);
    try {
      const r = await axios.get(`${BASE}/template/${path}`, { headers, params, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = path === 'holidays' ? 'holidays-template.xlsx' : `admissions-class-${params.class}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      notify('success', 'Template downloaded');
    } catch (e) {
      notify('error', await errMsg(e));
    } finally { setBusy(''); }
  };

  const upload = async (path, file, params, key) => {
    setBusy(key); setDetails([]);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${BASE}/upload/${path}`, fd, { headers, params });
      const d = r.data;
      notify('success', path === 'holidays'
        ? `${d.imported} holiday(s) imported${d.skipped ? `, ${d.skipped} row(s) skipped` : ''}`
        : `${d.updated} admission date(s) updated${d.skipped ? `, ${d.skipped} row(s) skipped` : ''}`);
      setDetails(d.errors || []);
    } catch (e) {
      notify('error', await errMsg(e));
      setDetails(e.response?.data?.errors || []);
    } finally { setBusy(''); }
  };

  const btn = 'w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-white shadow transition disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 p-4 md:p-8">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 max-w-sm rounded-lg px-4 py-3 text-sm font-medium text-white shadow-lg ${toast.type === 'success' ? 'bg-emerald-600' : 'bg-red-600'}`}>
          {toast.type === 'success' ? '✅ ' : '⚠️ '}{toast.message}
        </div>
      )}

      <div className="mx-auto max-w-5xl">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-800">Bulk Data Manager</h1>
        <p className="text-slate-500 mt-1 mb-6">Upload holidays and student admission dates with Excel. Attendance sheets update automatically.</p>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl bg-white p-6 shadow-lg ring-1 ring-slate-100">
            <h2 className="text-lg font-bold text-slate-800">🏖️ Manage School Holidays</h2>
            <p className="text-sm text-slate-500 mt-1 mb-4">Applies to the whole school. Attendance shows “H” on these dates.</p>
            <button className={`${btn} bg-indigo-600 hover:bg-indigo-700 mb-4`} disabled={!!busy}
              onClick={() => download('holidays', {}, 'dl-h')}>
              {busy === 'dl-h' ? 'Preparing...' : '⬇️ Download Holiday Template'}
            </button>
            <Dropzone busy={busy === 'up-h'} onFile={(f) => upload('holidays', f, {}, 'up-h')} />
          </div>

          <div className="rounded-2xl bg-white p-6 shadow-lg ring-1 ring-slate-100">
            <h2 className="text-lg font-bold text-slate-800">🎓 Manage Student Admission Dates</h2>
            <p className="text-sm text-slate-500 mt-1 mb-4">Attendance before the admission date shows “N/A” and cannot be edited.</p>
            <select value={className} onChange={(e) => setClassName(e.target.value)}
              className="w-full mb-3 rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500">
              <option value="">Select class</option>
              {classes.map((c) => <option key={c} value={c}>Class {c}</option>)}
            </select>
            <button className={`${btn} bg-emerald-600 hover:bg-emerald-700 mb-4`} disabled={!className || !!busy}
              onClick={() => download('admissions', { class: className }, 'dl-a')}>
              {busy === 'dl-a' ? 'Preparing...' : '⬇️ Download Class Template'}
            </button>
            {className
              ? <Dropzone busy={busy === 'up-a'} onFile={(f) => upload('admissions', f, { class: className }, 'up-a')} />
              : <p className="text-center text-xs text-slate-400 py-6">Select a class to enable upload</p>}
          </div>
        </div>

        {details.length > 0 && (
          <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm font-semibold text-amber-800 mb-2">Skipped rows</p>
            <ul className="list-disc pl-5 text-xs text-amber-700 space-y-0.5">
              {details.map((d, i) => <li key={i}>{d}</li>)}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
